import { videoEditor } from '@mi-front/api-clients';
import { NullState } from '@mi-front/ui';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Grid, Typography } from '@mui/material';
import { QueryView } from '@ui/utils/async-view/QueryView';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useFragment } from '../../hooks/useFragment';
import { CloneVideoEditionProjectDialog } from './CloneVideoEditionProjectDialog';
import { CreateVideoEditionProjectDialog } from './CreateVideoEditionProjectDialog';
import { ProjectCard } from './ProjectCard';

/**
 * Tab content for video editions: list of video editor projects linked to the fragment.
 * Shows each project in an MUI Card with create, clone, delete, open-in-editor, and play-generated actions.
 */
export function FragmentVideoEditionsTab() {
  const { fragment } = useFragment();
  const [createOpen, setCreateOpen] = useState(false);
  const [cloneProject, setCloneProject] = useState<videoEditor.Project | null>(null);

  const listQuery = videoEditor.useProjectsList({
    sourceType: 'FRAGMENT',
    sourceId: fragment.id,
  });

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h6">
          <FormattedMessage id="fragments.videoEditions.title" defaultMessage="Ediciones" />
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          size="small"
        >
          <FormattedMessage
            id="fragments.videoEditions.createProject"
            defaultMessage="Crear proyecto"
          />
        </Button>
      </Box>

      <QueryView query={listQuery}>
        {(projects) => {
          if (!projects.length) {
            return (
              <NullState className="p-6">
                <FormattedMessage
                  id="fragments.videoEditions.empty"
                  defaultMessage="No hay proyectos de edición. Creá uno para editar el video del fragmento."
                />
              </NullState>
            );
          }

          return (
            <Grid container spacing={2}>
              {projects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project as videoEditor.Project}
                  onClone={() => setCloneProject(project as videoEditor.Project)}
                />
              ))}
            </Grid>
          );
        }}
      </QueryView>

      <CreateVideoEditionProjectDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      {cloneProject && (
        <CloneVideoEditionProjectDialog
          open={!!cloneProject}
          onClose={() => setCloneProject(null)}
          sourceProject={cloneProject}
        />
      )}
    </>
  );
}
